const n = (valeur) => Number.isFinite(Number(valeur)) ? Number(valeur) : 0;
const nom = (valeur) => String(valeur || "").trim().toLocaleLowerCase("fr-FR");

export const MARGE_NON_CERTIFIEE = "NON_CERTIFIEE";

export function transformationCmdDevis(transformes = 0, devis = 0) {
  const numerateur = n(transformes);
  const denominateur = n(devis);
  return {
    numerateur,
    denominateur,
    taux: denominateur > 0 ? numerateur / denominateur * 100 : 0,
    formule: "devis transformés en commande ÷ nombre total de devis × 100"
  };
}

export function tauxAtteinte(realise = 0, objectif = 0) {
  return n(objectif) > 0 ? n(realise) / n(objectif) * 100 : 0;
}

export function planIndividuelParDefaut(commercial = "") {
  return {
    commercial, version: 1, valideDu: "", valideAu: null,
    objectifs: { caEncaisse: 0, visites: 0, cmdDevisPct: 0, margeMode: "MARGE_EUROS", margeValeur: 0 },
    composantes: {
      ca: { active: true, modeCalcul: "TOTAL", paliers: [] },
      surprime: { active: false, modeCalcul: "TOTAL", paliers: [] },
      visites: { active: false, modeCalcul: "TOTAL", paliers: [] },
      cmdDevis: { active: false, modeCalcul: "TOTAL", paliers: [] },
      marge: { active: false, modeCalcul: "TOTAL", paliers: [] }
    }
  };
}

const paliersTries = (paliers = []) => [...paliers].map((p) => ({
  debut: Math.max(0, n(p.debut ?? p.seuil)), fin: p.fin == null || p.fin === "" ? Infinity : Math.max(0, n(p.fin)),
  type: String(p.type || "MONTANT").toUpperCase(), valeur: n(p.valeur)
})).sort((a, b) => a.debut - b.debut);

export function calculerPaliers({ realise = 0, objectif = 0, paliers = [], modeCalcul = "TOTAL", base = realise }) {
  const atteinte = tauxAtteinte(realise, objectif);
  const liste = paliersTries(paliers);
  if (!liste.length || n(objectif) <= 0) return { prime: 0, atteinte, details: [] };
  if (modeCalcul === "TOTAL") {
    const palier = liste.filter((p) => atteinte >= p.debut && atteinte < p.fin).at(-1) || liste.filter((p) => atteinte >= p.debut).at(-1);
    if (!palier) return { prime: 0, atteinte, details: [] };
    const prime = palier.type === "TAUX" ? n(base) * palier.valeur / 100 : palier.valeur;
    return { prime, atteinte, details: [{ ...palier, base: n(base), prime }] };
  }
  const details = liste.map((palier) => {
    const borneBasse = n(objectif) * palier.debut / 100;
    const borneHaute = palier.fin === Infinity ? n(realise) : n(objectif) * palier.fin / 100;
    const baseTranche = Math.max(0, Math.min(n(realise), borneHaute) - borneBasse);
    const prime = baseTranche <= 0 ? 0 : palier.type === "TAUX" ? baseTranche * palier.valeur / 100 : palier.valeur;
    return { ...palier, base: baseTranche, prime };
  }).filter((p) => p.base > 0);
  return { prime: details.reduce((s, p) => s + p.prime, 0), atteinte, details };
}

const calculComposante = (config, realise, objectif, base = realise) => config?.active
  ? calculerPaliers({ realise, objectif, base, paliers: config.paliers, modeCalcul: config.modeCalcul })
  : { prime: 0, atteinte: tauxAtteinte(realise, objectif), details: [] };

export function calculerPlanIndividuel({ plan, resultats = {}, margeCertifiee = false }) {
  const p = { ...planIndividuelParDefaut(plan?.commercial), ...(plan || {}), objectifs: { ...planIndividuelParDefaut().objectifs, ...(plan?.objectifs || {}) }, composantes: { ...planIndividuelParDefaut().composantes, ...(plan?.composantes || {}) } };
  if (nom(p.commercial) === "non attribué" || nom(p.commercial) === "non attribue") return { eligible: false, motif: "NON_ATTRIBUE", personnelle: 0, surprime: 0, total: 0, composantes: {} };
  const ca = calculComposante(p.composantes.ca, resultats.caEncaisse, p.objectifs.caEncaisse, resultats.caEncaisse);
  const surprime = calculComposante(p.composantes.surprime, resultats.caEncaisse, p.objectifs.caEncaisse, resultats.caEncaisse);
  const visites = calculComposante(p.composantes.visites, resultats.visites, p.objectifs.visites, resultats.visites);
  const cmdDevis = calculComposante(p.composantes.cmdDevis, resultats.cmdDevisPct, p.objectifs.cmdDevisPct, resultats.cmdDevisPct);
  const marge = p.composantes.marge?.active && margeCertifiee
    ? calculComposante(p.composantes.marge, resultats.marge, p.objectifs.margeValeur, resultats.marge)
    : { prime: null, atteinte: null, details: [], disponibilite: MARGE_NON_CERTIFIEE };
  const personnelle = ca.prime + visites.prime + cmdDevis.prime + (marge.prime || 0);
  return { eligible: true, personnelle, surprime: surprime.prime, total: personnelle + surprime.prime, composantes: { ca, visites, cmdDevis, marge, surprime } };
}

export function calculerPrimeManagement({ relation, resultatsEncadre = {}, margeCertifiee = false }) {
  if (!relation?.active) return { management: 0, surprime: 0, total: 0, composantes: {} };
  const plan = { ...planIndividuelParDefaut(relation.responsable), ...(relation.plan || {}), commercial: relation.responsable };
  const resultat = calculerPlanIndividuel({ plan, resultats: resultatsEncadre, margeCertifiee });
  return { management: resultat.personnelle, surprime: resultat.surprime, total: resultat.total, composantes: resultat.composantes, relation: `${relation.responsable} → ${relation.commercialEncadre}` };
}

export function simulerPrimes({ plan, resultats, relations = [], resultatsParCommercial = {}, margeCertifiee = false }) {
  const personnel = calculerPlanIndividuel({ plan, resultats, margeCertifiee });
  const management = relations.filter((r) => r.active && nom(r.responsable) === nom(plan?.commercial)).map((relation) => calculerPrimeManagement({ relation, resultatsEncadre: resultatsParCommercial[relation.commercialEncadre] || {}, margeCertifiee }));
  const totalManagement = management.reduce((s, x) => s + x.management, 0);
  const totalSurprimeManagement = management.reduce((s, x) => s + x.surprime, 0);
  return { personnel, management, primePersonnelle: personnel.personnelle, primeManagement: totalManagement, surprime: personnel.surprime + totalSurprimeManagement, total: personnel.total + management.reduce((s, x) => s + x.total, 0), simulation: true, ecritures: 0 };
}

export function snapshotPlan({ plan, calcul, dateCalcul = new Date().toISOString() }) {
  return JSON.parse(JSON.stringify({ versionPlan: plan.version, valideDu: plan.valideDu, valideAu: plan.valideAu, commercial: plan.commercial, plan, calcul, dateCalcul }));
}
